/**
 * 物料协议。materials 包实现它，editor 与 runtime 消费它。
 * 物料组件必须是纯函数组件（hooks 允许），不得读 window、不得直接发请求，
 * 一切能力通过 ctx 注入。
 */
import type { ComponentType, ReactNode } from 'react';
import type { z } from 'zod';
import type { Action, Layer } from './document';

/** 属性面板 setter 类型。editor 里有对应的注册表。 */
export type SetterType =
  | 'input' | 'textarea' | 'number' | 'slider' | 'color' | 'select' | 'radio'
  | 'switch' | 'image' | 'video' | 'audio' | 'link' | 'font' | 'formFields';

/** 挂在 zod 字段 .meta() 上的面板描述。 */
export interface PropMeta {
  label: string;
  setter: SetterType;
  /** 面板分组，默认 '属性' */
  group?: string;
  /** select/radio 选项 */
  options?: { label: string; value: string | number | boolean }[];
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  /** 根据其他 props 决定是否显示。注意：函数不可序列化，propsSchema 不得跨进程传输 */
  visible?: (props: Record<string, unknown>) => boolean;
  /** image setter 多选（gallery 用） */
  multiple?: boolean;
  /** 兼容 zod v4 .meta() 的参数类型 */
  [key: string]: unknown;
}

export type MaterialMode = 'edit' | 'view';

/** 运行时/编辑器注入给物料组件的上下文。 */
export interface MaterialContext {
  mode: MaterialMode;
  layer: Layer;
  /** 当前缩放比（设计像素 → 屏幕像素） */
  scale: number;
  nav: {
    gotoPage(pageId: string): void;
    next(): void;
    prev(): void;
    openPopup(popupId: string): void;
    closePopup(): void;
  };
  /** 触发图层上绑定的 action（物料内部按钮点击等） */
  dispatch(action: Action): void;
  api: {
    /** 表单提交，返回 Promise，失败抛错 */
    submitForm(formId: string, data: Record<string, unknown>): Promise<void>;
  };
  assets: {
    /** 资源地址处理（CDN 前缀、图片样式），物料不要自己拼 URL */
    url(src: string, opts?: { width?: number }): string;
  };
  /** 编辑态下的 props 局部更新（如文本双击编辑） */
  updateProps?(patch: Record<string, unknown>): void;
}

export interface MaterialComponentProps<P = Record<string, unknown>> {
  props: P;
  ctx: MaterialContext;
  children?: ReactNode;
}

export interface MaterialCapabilities {
  resizable: boolean;
  rotatable: boolean;
  /** 是否可以容纳子图层（v1 只有 form 用） */
  container: boolean;
  /** 双击进入行内编辑 */
  inlineEdit: boolean;
  /** 保持宽高比缩放 */
  keepRatio: boolean;
}

export interface MaterialDefinition<P extends Record<string, unknown> = Record<string, unknown>> {
  id: string;
  version: number;
  name: string;
  category: 'basic' | 'media' | 'interactive';
  /** 物料库里的图标（React 组件或 emoji/字符） */
  icon: ComponentType | string;
  defaultSize: { w: number; h: number };
  defaultProps: P;
  /** zod object schema，字段 .meta() 必须是 PropMeta */
  propsSchema: z.ZodType<P>;
  capabilities: MaterialCapabilities;
  component: ComponentType<MaterialComponentProps<P>>;
  /** key 为旧版本号，把该版本 props 迁到下一版 */
  migrate: Record<number, (props: Record<string, unknown>) => Record<string, unknown>>;
}

const DEFAULT_CAPABILITIES: MaterialCapabilities = {
  resizable: true, rotatable: true, container: false, inlineEdit: false, keepRatio: false,
};

export function defineMaterial<P extends Record<string, unknown>>(
  def: Omit<MaterialDefinition<P>, 'capabilities' | 'migrate'> & {
    capabilities?: Partial<MaterialCapabilities>;
    migrate?: MaterialDefinition<P>['migrate'];
  },
): MaterialDefinition<P> {
  return {
    ...def,
    capabilities: { ...DEFAULT_CAPABILITIES, ...def.capabilities },
    migrate: def.migrate ?? {},
  };
}

/** 把图层 props 迁移到物料当前版本。 */
export function migrateLayerProps(
  def: MaterialDefinition,
  layer: Layer,
): { props: Record<string, unknown>; materialVersion: number } {
  let props = layer.props;
  let v = layer.materialVersion;
  while (v < def.version) {
    const fn = def.migrate[v];
    if (!fn) throw new Error(`物料 ${def.id} 缺少 v${v} → v${v + 1} 的迁移函数`);
    props = fn(props);
    v += 1;
  }
  return { props, materialVersion: v };
}
